import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * Buttons follow the pipeline design: condensed display type, uppercase, wide
 * tracking, 2px corners. `default` is the design's solid, `outline` its ghost,
 * `ghost` its quiet.
 */
const buttonVariants = cva(
  // A solid button (white text on black) faded to 40% washes both toward the
  // page background at nearly the same rate, so the label all but disappears
  // right when it matters most — mid-save. 70% keeps it dimmed but legible.
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm border font-display text-note font-semibold tracking-[0.06em] whitespace-nowrap uppercase transition-[background-color,color,filter] outline-none disabled:pointer-events-none disabled:opacity-70 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      variant: {
        default:
          "border-primary bg-primary text-primary-foreground hover:brightness-[0.94]",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground hover:brightness-[0.94]",
        outline: "border-border bg-transparent text-foreground hover:bg-accent",
        secondary:
          "border-secondary bg-secondary text-secondary-foreground hover:brightness-[0.94]",
        ghost: "border-transparent bg-transparent text-muted-foreground hover:bg-accent",
        link: "border-transparent text-pine normal-case tracking-normal underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[38px] px-3.5 py-2",
        xs: "h-6 gap-1 px-2 text-meta tracking-[0.08em] [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 text-meta",
        lg: "h-11 px-6",
        icon: "size-[38px]",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
